import { patientDisplayName } from "src-next/modules/clinical-insights/helpers/insightPractitionerUtils";
import type { InsightChartItem, InsightPatientRow } from "src-next/modules/clinical-insights/types/insightWorkspaceTypes";

const IGNORED_CODES = new Set(["Não informado", ""]);

export const MEDICATION_STATUS_TRANSLATION: Record<string, string> = {
  active: "Ativo",
  "on-hold": "Suspenso",
  cancelled: "Cancelado",
  completed: "Concluído",
  "entered-in-error": "Erro",
  stopped: "Interrompido",
  draft: "Solicitado",
  unknown: "Desconhecido",
};

function medicationLabel(med: fhir5.MedicationRequest): { code: string; label: string } {
  const codingWithCode = med.medication?.concept?.coding?.find((c) => !!c.code);
  const code = codingWithCode?.code || "Não informado";
  const label =
    codingWithCode?.display || med.medication?.concept?.text || code;
  return { code, label };
}

function medicationDate(med: fhir5.MedicationRequest): string {
  if (med.authoredOn) return new Date(med.authoredOn).toLocaleDateString();
  if (med.effectiveDosePeriod?.start) {
    return new Date(med.effectiveDosePeriod.start).toLocaleDateString();
  }
  if (med.meta?.lastUpdated) return new Date(med.meta.lastUpdated).toLocaleDateString();
  return "Não informado";
}

function countsForRanking(medications: fhir5.MedicationRequest[]) {
  const counts = new Map<string, { label: string; value: number }>();
  for (const med of medications) {
    const { code, label } = medicationLabel(med);
    if (
      IGNORED_CODES.has(code) ||
      !med.medication?.concept?.text ||
      label === "Não informado"
    ) {
      continue;
    }
    const prev = counts.get(code);
    counts.set(code, { label, value: (prev?.value ?? 0) + 1 });
  }
  return counts;
}

export function buildMedicationInsightCharts(medications: fhir5.MedicationRequest[]) {
  const statusMap = new Map<string, number>();
  for (const med of medications) {
    const status = med.status || "unknown";
    statusMap.set(status, (statusMap.get(status) || 0) + 1);
  }
  const total = medications.length;
  const statusItems: InsightChartItem[] = Array.from(statusMap.entries())
    .map(([key, value]) => ({
      key,
      label:
        MEDICATION_STATUS_TRANSLATION[key] ||
        key.charAt(0).toUpperCase() + key.slice(1),
      value,
    }))
    .sort((a, b) => b.value - a.value);

  const rankingItems: InsightChartItem[] = Array.from(
    countsForRanking(medications).entries()
  )
    .map(([key, { label, value }]) => ({ key, label, value }))
    .sort((a, b) => b.value - a.value);

  return { statusItems, rankingItems, total };
}

export function buildMedicationPatientRows(
  medications: fhir5.MedicationRequest[],
  patients: fhir5.Patient[]
): InsightPatientRow[] {
  const patientMap = new Map<string, fhir5.Patient>();
  for (const patient of patients) {
    if (patient.id) patientMap.set(patient.id, patient);
  }

  const groups = new Map<string, InsightPatientRow>();
  for (const med of medications) {
    const patientRef = med.subject?.reference;
    if (!patientRef) continue;
    const patientId = patientRef.split("/").pop() || "";
    if (!patientId) continue;

    const { code, label } = medicationLabel(med);
    const statusKey = med.status || "unknown";

    if (!groups.has(patientId)) {
      groups.set(patientId, {
        patientId,
        patientName: patientDisplayName(patientMap.get(patientId)),
        patient: patientMap.get(patientId),
        items: [],
      });
    }
    groups.get(patientId)!.items.push({
      itemKey: code,
      label,
      statusKey,
      statusLabel:
        MEDICATION_STATUS_TRANSLATION[statusKey] ||
        statusKey.charAt(0).toUpperCase() + statusKey.slice(1),
      practitioner: med.requester?.display || "Não informado",
      date: medicationDate(med),
    });
  }

  return Array.from(groups.values()).sort((a, b) => b.items.length - a.items.length);
}
